import api from './api';
import { User } from '../types';

export const userService = {
  listUsers: async (params?: { page?: number; per_page?: number; role?: string; q?: string }) => {
    const response = await api.get<{
      users: User[];
      total: number;
      page: number;
      per_page: number;
      pages: number;
    }>('/users', { params });
    return response.data;
  },

  updateRole: async (userId: number, role: User['role']) => {
    const response = await api.put<{ message: string; user: User }>(`/users/${userId}/role`, { role });
    return response.data;
  },

  createUser: async (payload: {
    username: string;
    email: string;
    password: string;
    full_name?: string;
    avatar?: string;
    phone?: string;
    address?: string;
    role?: User['role'];
  }) => {
    const response = await api.post<{ message: string; user: User }>(`/users`, payload);
    return response.data;
  },

  updateUser: async (
    userId: number,
    payload: Partial<Pick<User, 'username' | 'email' | 'full_name' | 'role' | 'avatar' | 'phone' | 'address'>> & {
      password?: string;
    }
  ) => {
    const response = await api.put<{ message: string; user: User }>(`/users/${userId}`, payload);
    return response.data;
  },

  deleteUser: async (userId: number) => {
    const response = await api.delete<{ message: string }>(`/users/${userId}`);
    return response.data;
  },

  uploadAvatar: async (userId: number, file: File) => {
    const formData = new FormData();
    formData.append('avatar', file);
    const response = await api.post<{ message: string; user: User }>(`/users/${userId}/avatar`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.user;
  },

  unlockUser: async (userId: number) => {
    const response = await api.post<{ message: string; user: User }>(`/users/${userId}/unlock`);
    return response.data;
  },

  lockUser: async (userId: number, payload: { reason?: string }) => {
    const response = await api.post<{ message: string; user: User }>(`/users/${userId}/lock`, payload);
    return response.data;
  },

  getLockedUsers: async () => {
    const response = await api.get<{ users: User[]; total: number }>('/users/locked');
    return response.data;
  },

  updateProfile: async (payload: {
    full_name?: string;
    email?: string;
    phone?: string;
    address?: string;
    password?: string;
    avatar?: string;
  }) => {
    const response = await api.put<{ message: string; user: User }>('/users/profile', payload);
    return response.data;
  },

  getUserProfile: async (userId: number) => {
    const response = await api.get<{ user: User }>(`/users/${userId}`);
    return response.data.user;
  },

  getUserProfileByUsername: async (username: string) => {
    // Username is already encoded by React Router, but we need to ensure it's properly encoded
    // Flask will automatically decode it
    const response = await api.get<{ user: User }>(`/users/username/${encodeURIComponent(username)}`);
    return response.data.user;
  },

  toggleKeyAccess: async (userId: number, canViewKeys: boolean) => {
    const response = await api.put<{ message: string; user: User }>(`/users/${userId}/toggle-key-access`, {
      can_view_keys: canViewKeys,
    });
    return response.data;
  },

  changeName: async (first_name: string, last_name: string) => {
    const response = await api.post<{ message: string; user: User }>('/users/change-name', {
      first_name,
      last_name,
    });
    return response.data;
  },

  updateProfileSettings: async (payload: {
    avatar?: string;
    bio?: string;
    location?: string;
    first_name?: string;
    last_name?: string;
  }) => {
    const response = await api.put<{ message: string; user: User }>('/users/settings/profile', payload);
    return response.data;
  },

  updateAccountSettings: async (payload: {
    email?: string;
    phone?: string;
  }) => {
    const response = await api.put<{ message: string; user: User }>('/users/settings/account', payload);
    return response.data;
  },

  changePassword: async (payload: {
    current_password: string;
    new_password: string;
  }) => {
    const response = await api.post<{ message: string }>('/users/settings/change-password', payload);
    return response.data;
  },

  getFakeUsersSetting: async () => {
    const response = await api.get<{ fake_users_enabled: boolean; total_fake_users: number }>('/users/settings/fake-users');
    return response.data;
  },

  toggleFakeUsers: async (enabled: boolean) => {
    const response = await api.put<{ message: string; fake_users_enabled: boolean }>('/users/settings/fake-users', { enabled });
    return response.data;
  },

  getCommunityStats: async () => {
    const response = await api.get<{ total_members: number; online_members: number; total_admins: number }>('/users/stats');
    return response.data;
  },

  listPublicUsers: async (params?: { page?: number; per_page?: number; role?: string; q?: string }) => {
    const response = await api.get<{
      users: User[];
      total: number;
      page: number;
      per_page: number;
      pages: number;
    }>('/users/public', { params });
    return response.data;
  },

  // Fake Users Management
  listFakeUsers: async (params?: { page?: number; per_page?: number; q?: string; online_only?: boolean }) => {
    const response = await api.get<{
      fake_users: (User & { is_online?: boolean })[];
      total: number;
      page: number;
      per_page: number;
      pages: number;
    }>('/users/fake-users', { params });
    return response.data;
  },

  createFakeUser: async (payload: { full_name: string }) => {
    const response = await api.post<{ message: string; user: User }>('/users/fake-users', payload);
    return response.data;
  },

  updateFakeUser: async (userId: number, payload: { full_name?: string; is_online?: boolean }) => {
    const response = await api.put<{ message: string; user: User }>(`/users/fake-users/${userId}`, payload);
    return response.data;
  },

  deleteFakeUser: async (userId: number) => {
    const response = await api.delete<{ message: string }>(`/users/fake-users/${userId}`);
    return response.data;
  },

  bulkDeleteFakeUsers: async (userIds: number[]) => {
    const response = await api.delete<{ message: string; deleted_count: number }>('/users/fake-users/bulk', {
      data: { user_ids: userIds }
    });
    return response.data;
  },

  uploadFakeUserAvatar: async (userId: number, file: File) => {
    const formData = new FormData();
    formData.append('avatar', file);
    const response = await api.post<{ message: string; user: User }>(`/users/fake-users/${userId}/avatar`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  getFakeUsersConfig: async () => {
    const response = await api.get<{
      new_users_per_hour_min: number;
      new_users_per_hour_max: number;
      target_online_percent: number;
    }>('/users/settings/fake-users-config');
    return response.data;
  },

  updateFakeUsersConfig: async (config: {
    new_users_per_hour_min?: number;
    new_users_per_hour_max?: number;
    target_online_percent?: number;
  }) => {
    const response = await api.put<{
      message: string;
      new_users_per_hour_min: number;
      new_users_per_hour_max: number;
      target_online_percent: number;
    }>('/users/settings/fake-users-config', config);
    return response.data;
  },
};


